<template>
    <div class="align-middle delivery-info grid-x align-justify">
        <div class="cell shrink">
            <p class="display-large color-dark-gray cart-title semi-bold" v-if="isDelivery">Delivery Info</p>
            <p class="display-large color-dark-gray cart-title semi-bold" v-else>Pick up at {{ storeName }}</p>
        </div>
        <div class="cell shrink" v-if="canBeEdited">
            <p class="cart-edit cart-title"><a @click="editMode = !editMode">{{ (editMode) ? 'Close' : 'Edit' }}</a></p>
        </div>
        <div class="cell" v-if="isDelivery && deliveryLabel">
            <p class="text-left display-small color-dark-gray  semi-bold delivery-fee">{{ deliveryLabel }}</p>
        </div>
        <div class="cell" v-if="!editMode">
            <div class="address-details" v-if="isDelivery">
                <p>{{ deliveryAddress }}</p>
                <p>{{ businessName }}</p>
                <p>{{ deliveryInstructions }}</p>
            </div>
            <p v-else>{{ storeAddress }}</p>
        </div>
        <div class="cell" v-if="editMode && isDelivery">
            <address-field v-if="mapsApiKey"
                country="ky"
                :apiKey="mapsApiKey"
                :deliveryAddress="deliveryAddress"
                :addressPlaceId="addressPlaceId"
                :storePlaceId="storePlaceId"
                :maxDeliveryDistance="maxDeliveryDistance"
                @change="deliveryAddress = $event"
                @onPredictionUpdate="addressPlaceId = $event"
                @addressValid="onAddressValidation($event)"
                :error-message="errorMessage('deliveryAddress')">
            </address-field>
            <text-input
                label="Business Name / Residence Name / Unit number"
                type="text"
                autocomplete="address-line2"
                :value="businessName"
                @change="businessName = $event"
                @blur="validateField('businessName')"
                :error-message="errorMessage('businessName')"
            ></text-input>
            <text-input
                placeholder="Delivery Instructions - e.g: Floor / Ring bell (optional)"
                :textarea="true"
                type="text"
                :value="deliveryInstructions"
                @change="deliveryInstructions = $event"
                autocomplete="off"
                @blur="validateField('deliveryInstructions')"
                :error-message="errorMessage('deliveryInstructions')"
            ></text-input>
            <p>
            <checkbox
                :checked="saveAddress"
                @change="saveAddress = $event"
                label="Save as default address"
            ></checkbox>
            </p>
        </div>
    </div>
</template>
<script lang="ts">
import TextInput from '@/components/common/forms/TextInput.vue';
import Checkbox from '@/components/common/ui/Checkbox.vue';
import AddressField from '@/components/common/forms/AddressField.vue';
import { AddressStatusResponse } from '@/utils/types';
import CartModule from '@/stores/modules/cart';
import UserModule from '@/stores/modules/user';
import Vue from 'vue';
import Component from 'vue-class-component';
import { getModule } from 'vuex-module-decorators';
import { PaymentOption } from '../../../models/order';
import TimeSchedulerModule, { OrderRetrievalMethod } from '../../../stores/modules/time-scheduler';
import Settings, { SettingNamespace } from '../../../utils/settings';

@Component({
    components: { TextInput, Checkbox, AddressField }
})
export default class CartDeliveryInfo extends Vue
{
    userModule!: UserModule;
    cartModule!: CartModule;
    timeSchedulerModule!: TimeSchedulerModule;
    editMode: boolean = true;
    storePlaceId: string = '';
    storeAddress: string = '';
    storeName: string = '';
    storePhoneNumber: string = '';
    feePerMile: number = 0;
    maxDeliveryDistance: number = 0;
    maxDeliveryFee: number = 0;
    minDeliveryFee: number = 0;
    mapsApiKey: string = '';
    deliveryLabel: string = '';

    async created() {
        this.userModule = getModule(UserModule, this.$store);
        this.cartModule = getModule(CartModule, this.$store);
        this.timeSchedulerModule = getModule(TimeSchedulerModule, this.$store);
        const settings = await Settings.getInstance();
        this.storeName = settings.get(SettingNamespace.GENERAL, 'store_name')?.value;
        this.storeAddress = settings.get(SettingNamespace.CONTENT, 'store_address')?.value;
        this.storePhoneNumber = settings.get(SettingNamespace.CONTENT, 'contact_phone')?.value;
        this.storePlaceId = settings.get(SettingNamespace.ORDERS, 'store_place_id')?.value;
        this.feePerMile = parseFloat(settings.get(SettingNamespace.ORDERS, 'fee_per_mile')?.value);
        this.minDeliveryFee = parseFloat(settings.get(SettingNamespace.ORDERS, 'min_delivery_fee')?.value);
        this.maxDeliveryFee = parseFloat(settings.get(SettingNamespace.ORDERS, 'max_delivery_fee')?.value);
        this.maxDeliveryDistance = parseFloat(settings.get(SettingNamespace.ORDERS, 'max_distance')?.value);
        this.mapsApiKey = settings.get(SettingNamespace.GENERAL, 'maps_api_key')?.value;
        this.deliveryLabel = (settings.get(SettingNamespace.ORDERS, 'delivery_label')!.value) ? settings.get(SettingNamespace.ORDERS, 'delivery_label')!.value : '';
    }

    mounted() {
        if (this.user) {
            if (this.user.default_address && !this.cartModule.checkoutFields.deliveryAddress.value) {
                this.cartModule.setDeliveryAddress(this.user.default_address);
            }
            // if (this.user.address_place_id && !this.cartModule.userAddressPlaceId) {
            //     this.cartModule.setAddressPlaceId(this.user.address_place_id);
            // }
            if (this.user.business_name && !this.cartModule.checkoutFields.businessName.value) {
                this.cartModule.setBusinessName(this.user.business_name);
            }
            if (this.user.delivery_instructions && !this.cartModule.checkoutFields.deliveryInstructions.value) {
                this.cartModule.setDeliveryInstructions(this.user.delivery_instructions);
            }
        }
        if ((!this.deliveryAddress.length || !this.businessName.length) && this.retrievalMethod === OrderRetrievalMethod.DELIVERY) {
            this.editMode = true;
        }
        this.$watch('retrievalMethod', (retrievalMethod: OrderRetrievalMethod) => {
            if (retrievalMethod === OrderRetrievalMethod.PICK_UP) {
                this.editMode = false;
            } else {
                this.editMode = true;
            }
        }, {immediate: true});
    }

    validateField(field: string) {
        this.cartModule.validateCheckoutField(field);
    }

    onAddressValidation(response: {success: boolean, status: AddressStatusResponse, content?: any}) {
        if (response.success) {
            const deliveryFee = this.calculatedDeliveryFee(response.content);
            return this.cartModule.setDeliveryFee(deliveryFee);
        }

        this.deliveryFee = this.minDeliveryFee;
        return this.cartModule.addCheckoutError({
            source: 'deliveryAddress',
            message: this.formatAddressError(response.status)
        });
    }

    get formatAddressError() {
        return (status: AddressStatusResponse) => {
            switch(status) {
                case AddressStatusResponse.NOT_FOUND:
                    return 'Sorry, this address is not found. Please try another one or call us '+ this.storePhoneNumber +' to finalise your order.';
                case AddressStatusResponse.EXCEEDED_DISTANCE:
                    return 'Sorry, we don\'t deliver there. Please enter a different address or schedule a pick up.';
                case AddressStatusResponse.NOT_STREET_ADDRESS:
                    return 'Please enter an address with a valid street number.';
                case AddressStatusResponse.REVALIDATE_ADDRESS:
                    return 'Please enter a valid address.';
            }
        }
    }

    get calculatedDeliveryFee() {
        return (distanceInMiles: number) => {
            const totalFees = Math.round(((distanceInMiles * this.feePerMile) + Number.EPSILON) * 100) / 100;
            const deliveryFee = (totalFees >= this.maxDeliveryFee) ? this.maxDeliveryFee : (totalFees < this.minDeliveryFee) ? this.minDeliveryFee : totalFees;
            return deliveryFee;
        }

    }

    get AddressStatusResponse() {
        return AddressStatusResponse;
    }

    set deliveryFee(value: number) {
        this.cartModule.setDeliveryFee(value);
    }

    get deliveryFee() {
        return this.cartModule.deliveryFee;
    }

    get user() {
        return this.userModule.currentUser;
    }

    set deliveryAddress(value: string) {
        this.cartModule.clearCheckoutError('deliveryAddress');
        this.cartModule.setDeliveryAddress(value);
    }

    get deliveryAddress() {
        return this.cartModule.checkoutFields.deliveryAddress.value;
    }

    set addressPlaceId(value: string) {
        this.cartModule.setAddressPlaceId(value);
    }

    get addressPlaceId() {
        return this.cartModule.userAddressPlaceId;
    }

    get businessName() {
        return this.cartModule.checkoutFields.businessName.value;
    }

    set businessName(value: string) {
        this.cartModule.clearCheckoutError('businessName');
        this.cartModule.setBusinessName(value);
    }

    get deliveryInstructions() {
        return this.cartModule.checkoutFields.deliveryInstructions.value;
    }

    set deliveryInstructions(value: string) {
        this.cartModule.clearCheckoutError('deliveryInstructions');
        this.cartModule.setDeliveryInstructions(value);
    }

    get saveAddress() {
        return this.cartModule.saveAddress;
    }

    set saveAddress(value: boolean) {
        this.cartModule.setSaveAddress(value);
    }

    get paymentOption() {
        return this.cartModule.paymentOption;
    }

    set paymentOption(paymentOption: PaymentOption) {
        this.cartModule.setPaymentOption(paymentOption);
    }

    get PaymentOption() {
        return PaymentOption;
    }

    get retrievalMethod() {
        return this.timeSchedulerModule.retrievalMethod;
    }

    get isDelivery() {
        return this.retrievalMethod === OrderRetrievalMethod.DELIVERY;
    }

    get canBeEdited() {
        return this.deliveryAddress.length > 0 && this.businessName.length > 0 && this.retrievalMethod === OrderRetrievalMethod.DELIVERY;
    }

    get errorMessage() {
        return (field: string) => {
            const error = this.cartModule.checkoutErrors.find(error => error.source === field);
            if (error) {
                return error.message;
            }
        }
    }
}
</script>
<style lang="scss">
@import '~foundation-sites/scss/foundation';
@import '~scss/front/settings';

.address-details {
    p {
        margin: 0;
    }
}

</style>
